import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { categories, allShows } from "@/mocks/varietyShows";
import ShowCard from "@/pages/home/components/ShowCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { WebPageSchema, BreadcrumbListSchema } from "@/components/feature/StructuredData";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"rating" | "views" | "latest">("rating");

  const category = categories.find((c) => c.slug === slug);

  const filteredShows = useMemo(() => {
    if (!category) return [];
    const shows = allShows.filter((s) => {
      if (slug === "gaoxiao") {
        return s.category === "搞笑综艺" || s.category === "脱口秀";
      }
      return s.category === category.name;
    });

    if (sortBy === "rating") return [...shows].sort((a, b) => b.rating - a.rating);
    if (sortBy === "views") {
      return [...shows].sort((a, b) => {
        const aNum = parseFloat(a.views.replace("亿", "0000").replace("万", ""));
        const bNum = parseFloat(b.views.replace("亿", "0000").replace("万", ""));
        return bNum - aNum;
      });
    }
    return shows;
  }, [category, slug, sortBy]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h2 className="text-xl font-semibold text-foreground-700 mb-2">分类未找到</h2>
          <p className="text-foreground-400 mb-6">该综艺分类不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        name={`${category.name} - 红桃影院综艺在线观看`}
        url={`https://hongtao-movie.com/category/${slug}`}
        description={`红桃影院${category.name}专区，${category.description}。高清流畅不缓冲，免费在线观看。`}
      />
      <BreadcrumbListSchema
        items={[
          { name: "首页", url: "https://hongtao-movie.com" },
          { name: category.name, url: `https://hongtao-movie.com/category/${slug}` },
        ]}
      />

      <Breadcrumb />

      <main className="w-full px-4 md:px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
              <div className="flex bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setSortBy("rating")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === "rating"
                      ? "bg-background-50 text-foreground-900"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  评分最高
                </button>
                <button
                  onClick={() => setSortBy("views")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === "views"
                      ? "bg-background-50 text-foreground-900"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  热度最高
                </button>
                <button
                  onClick={() => setSortBy("latest")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === "latest"
                      ? "bg-background-50 text-foreground-900"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  最近更新
                </button>
              </div>
            </div>
          </div>

          {filteredShows.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredShows.map((show) => (
                <ShowCard
                  key={show.id}
                  id={show.id}
                  title={show.title}
                  category={show.category}
                  imageUrl={show.imageUrl}
                  rating={show.rating}
                  updateInfo={show.updateInfo}
                  views={show.views}
                  showCategory={false}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类下暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}